import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import api, { formatApiError } from "@/lib/api";
import { Inbox, AlertTriangle, Send, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminInbox() {
  const [emails, setEmails] = useState([]);
  const ctl = useListControls(emails, { name: (e) => e.to, fields: (e) => [e.to, e.subject], initialSort: "default" });
  const [expiring, setExpiring] = useState([]);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("emails");
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    const [e, ex] = await Promise.all([
      api.get("/admin/email-log?limit=50"),
      api.get(`/admin/credentials-expiring?days=${days}`),
    ]);
    setEmails(e.data);
    setExpiring(ex.data);
  };

  useEffect(() => { load();   }, [days]);

  const notify = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post(`/admin/credentials-expiring/notify?days=${days}`);
      toast.success(`Notified ${data.notified} coach${data.notified === 1 ? "" : "es"} (${data.items} credentials)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setNotifying(false); }
  };

  return (
    <div className="space-y-6">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">ADMIN · COMMS</div>
          <h1 className="h-display text-5xl tracking-tighter">Notifications &amp; inbox.</h1>
        </div>
        <div className="inline-flex border border-[#262626]" data-testid="inbox-tabs">
          {[{v:"emails",l:"Email log"},{v:"insurance",l:"Credentials expiry"}].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
              className="px-5 py-2.5 text-xs uppercase tracking-wider"
              style={tab === t.v ? { background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 } : { color: "#A3A3A3" }}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === "emails" && (
        <div className="card-surface p-6 fade-up d1">
          <div className="flex items-center gap-2 mb-4">
            <Inbox className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono">SENT MESSAGES · {emails.length}</div>
            <div className="ml-auto text-[0.65rem] text-[#A3A3A3] label-mono">PROVIDER: MOCK · LOGGED TO DB</div>
          </div>
          {!emails.length && <div className="text-sm text-[#A3A3A3]">No emails sent yet. Approve a coach or run an enrolment to generate one.</div>}
          {emails.length > 0 && <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search recipient or subject…" testid="inbox" className="mb-2" />}
          <div className="divide-y divide-[#262626]">
            {ctl.list.map((e) => (
              <div key={e.id} className="py-3" data-testid={`email-${e.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Mail className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  <span className="text-sm font-semibold">{e.to}</span>
                  <span className="label-mono text-[0.65rem]">· {e.kind}</span>
                  <span className="label-mono text-[0.65rem] ml-auto" style={{ color: e.status === "sent_mock" ? "#CCFF00" : "#FF4500" }}>{e.status}</span>
                </div>
                <div className="text-sm">{e.subject}</div>
                <div className="text-xs text-[#A3A3A3] whitespace-pre-wrap mt-1 line-clamp-3">{e.body}</div>
                <div className="text-[0.6rem] text-[#525252] label-mono mt-1">{e.created_at?.slice(0,16)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "insurance" && (
        <div className="space-y-4 fade-up d1">
          <div className="card-surface p-6 flex items-center gap-4 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-[#FF4500]" />
            <div className="label-mono">Show coaches whose insurance expires within</div>
            <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="days-input"
              className="w-20 bg-[#141414] border border-[#262626] px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF4500]" />
            <span className="label-mono">days</span>
            <button onClick={notify} disabled={notifying || !expiring.length} className="ml-auto px-4 py-2 border border-[#FF4500] text-[#FF4500] text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-30" data-testid="notify-btn">
              <Send className="w-3.5 h-3.5" /> {notifying ? "Sending…" : `Email ${expiring.length} coach${expiring.length===1?"":"es"}`}
            </button>
          </div>
          {!expiring.length ? (
            <div className="card-surface p-12 text-center text-sm text-[#A3A3A3]">No credentials expire within {days} days.</div>
          ) : (
            <div className="card-surface p-6 divide-y divide-[#262626]">
              {expiring.map((c, i) => {
                const danger = c.days_until_expiry <= 7;
                const color = c.kind === "insurance" ? "#FF4500" : "#CCFF00";
                return (
                  <div key={`${c.id}-${c.kind}-${i}`} className="py-3 flex items-center gap-4 flex-wrap" data-testid={`expiring-${c.id}-${c.kind}`}>
                    <span className="label-mono px-2 py-0.5" style={{ background: color + "22", color }}>{c.kind.toUpperCase()}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{c.name}</div>
                      <div className="text-xs text-[#A3A3A3]">{c.email} · {c.provider || "—"} {c.reference ? `#${c.reference}` : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="h-display text-2xl" style={{ color: danger ? "#FF4500" : color }}>{c.days_until_expiry}d</div>
                      <div className="label-mono text-[0.6rem]">expires {c.expires}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
